import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { getProperty } from "@/lib/helpers";

import { useBreakpoints } from "@/composables/useBreakpoints";

const FLAG = "mobile_web_optimization";

export type MobileWebDeviceClass = "phone" | "desktop";

/**
 * The single gate for the mobile web layout: whether it applies, and which
 * side of the breakpoint it is applying on.
 *
 * A near-verbatim extraction of useSheetPlacement.ts's gate: same flag, same
 * `useBreakpoints().type` threshold, same `flagsResolved` race guard.
 * `useFeatureFlag` cannot express `flagsResolved` — it reports false both for
 * "the flag is off" and "the tenant fetch has not answered yet" — and a
 * consumer keyed on it alone would render the wrong branch first and then
 * swap, which is exactly what this composable is built to avoid conflating.
 * See CompactAppHeader.vue's first-paint strategy for how this project's
 * header consumer spends that distinction.
 */
export function useMobileWebOptimization() {
  const store = useStore();
  const { type } = useBreakpoints();

  // Tracks the fetch having SETTLED rather than succeeded: `fetchTenant` has no
  // rejection handler, so on a failed request `tenant` stays null forever and a
  // gate keyed on the payload alone would never open.
  const fetchSettled = ref(false);

  // The sys-admin host serves no tenant: a fetch there only 404s into an error
  // toast, so the gate settles at once with the flag off.
  if (store.state.tenant || window.currentTenant?.sysAdminSignedIn) {
    fetchSettled.value = true;
  } else if (!store.state.fetchingTenant) {
    store.dispatch("fetchTenant");
  }

  // `fetchTenant` clears `fetchingTenant` in a `finally`, so the falling edge is
  // the one signal that covers both outcomes — including for a caller that did
  // not start the request itself.
  watch(
    () => store.state.fetchingTenant,
    (isFetching, wasFetching) => {
      if (wasFetching && !isFetching) fetchSettled.value = true;
    }
  );

  const flagsResolved = computed(
    () =>
      !!getProperty(store.state.tenant, "feature_flags") || fetchSettled.value
  );

  const flagEnabled = computed(() => {
    const flags = getProperty(store.state.tenant, "feature_flags");
    // The jbuilder view omits the key entirely when the flag is off, so a
    // missing key and an explicit false are the same answer.
    return flags ? !!flags[FLAG] : false;
  });

  const enabled = computed(
    () => flagsResolved.value && flagEnabled.value && type.value === "mobile"
  );

  // True while the answer for THIS viewport is still unknown: below the md
  // breakpoint either header could win, so the consumer holds an empty band
  // rather than painting V2 and swapping. At desktop width V2 is the only
  // possible outcome, so there is nothing to wait for.
  const pending = computed(
    () => type.value === "mobile" && !flagsResolved.value
  );

  // Which side of the breakpoint the flag is applying on, for behaviour that
  // differs per side rather than merely switching on. Null while the flag is
  // off or unresolved: there is then one behaviour and no side to name.
  const deviceClass = computed<MobileWebDeviceClass | null>(() => {
    if (!flagsResolved.value || !flagEnabled.value) return null;
    return type.value === "mobile" ? "phone" : "desktop";
  });

  return { enabled, flagsResolved, pending, deviceClass };
}
